import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, ShoppingBag } from 'lucide-react';
import { ReportHeader } from '@/components/reports/ReportUI';
import { ReportFilterBar, useReportFilters } from '@/components/reports/ReportFilterBar';
import api from '@/lib/api';
import { toast } from 'sonner';

const TYPE_COLORS = { Bill: 'bg-info/10 text-info', 'Purchase Order': 'bg-purple-50 text-purple-700', 'Bill Return': 'bg-orange-50 text-orange-700', 'Supplier Receipt': 'bg-success/10 text-success' };
const STATUS_COLORS = { draft: 'bg-muted text-muted-foreground', sent: 'bg-info/20 text-info', paid: 'bg-success/20 text-success', unpaid: 'bg-error/20 text-error', received: 'bg-success/20 text-success', approved: 'bg-success/20 text-success', cancelled: 'bg-error/20 text-error' };

export default function PurchaseTransactionsPage() {
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');

  const f = useReportFilters({ withUnassigned: true });
  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [f.params]);
  const fetchData = async () => {
    setLoading(true);
    try { const r = await api.get('/purchase-transactions', { params: f.params }); setTxns(r.data || []); } catch { toast.error('Failed to load'); } finally { setLoading(false); }
  };

  const filtered = txns.filter(t => {
    if (typeFilter !== 'all' && t.type !== typeFilter) return false;
    if (search && !t.reference_id?.toLowerCase().includes(search.toLowerCase()) && !t.supplier?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const totalAmount = filtered.reduce((s, t) => s + (t.amount || 0), 0);
  const totalOutstanding = filtered.reduce((s, t) => s + (t.outstanding || 0), 0);

  const exportSpec = () => ({
    title: 'Purchase Transactions', subtitle: `${f.range.start} → ${f.range.end} · ${f.branchLabel}`, filename: 'purchase_transactions', rows: filtered,
    cols: [{ key: 'reference_id', label: 'Reference' }, { key: 'type', label: 'Type' }, { key: 'supplier', label: 'Supplier' }, { key: 'date', label: 'Date' },
      { key: 'status', label: 'Status' }, { key: 'amount', label: 'Amount', numeric: true }, { key: 'outstanding', label: 'Outstanding', numeric: true }],
  });

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="purchase-transactions-page">
        <ReportHeader icon={ShoppingBag} title="Purchase Transactions" subtitle="Consolidated view of all purchase documents" testId="ptx-header" />
        <ReportFilterBar testId="ptx-filter" branches={f.branches} branchIds={f.branchIds} onBranchIds={f.setBranchIds} range={f.range} onRange={f.setRange}
          exportSpec={exportSpec} exportDisabled={!filtered.length} />
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Total Transactions</p><p className="text-xl font-bold">{filtered.length}</p></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Total Amount</p><p className="text-xl font-bold text-error">{totalAmount.toLocaleString('en', { minimumFractionDigits: 2 })}</p></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Outstanding</p><p className="text-xl font-bold text-warning">{totalOutstanding.toLocaleString('en', { minimumFractionDigits: 2 })}</p></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Document Types</p><p className="text-xl font-bold">{new Set(txns.map(t => t.type)).size}</p></CardContent></Card>
        </div>
        <div className="flex gap-3">
          <div className="relative flex-1 max-w-xs"><Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" /><Input placeholder="Search ref or supplier..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" /></div>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-48"><SelectValue placeholder="All Types" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="Bill">Bill</SelectItem>
              <SelectItem value="Purchase Order">Purchase Order</SelectItem>
              <SelectItem value="Bill Return">Bill Return</SelectItem>
              <SelectItem value="Supplier Receipt">Supplier Receipt</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Card><CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b"><tr>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Reference</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Type</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Supplier</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Date</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Status</th>
                <th className="text-right px-4 py-3 font-medium text-muted-foreground">Amount</th>
                <th className="text-right px-4 py-3 font-medium text-muted-foreground">Outstanding</th>
              </tr></thead>
              <tbody>
                {loading ? <tr><td colSpan={7} className="text-center py-8 text-muted-foreground">Loading...</td></tr> :
                 filtered.length === 0 ? <tr><td colSpan={7} className="text-center py-8 text-muted-foreground">No transactions found</td></tr> :
                 filtered.map(t => (
                  <tr key={t.id} className="border-b hover:bg-muted/50">
                    <td className="px-4 py-3 font-mono text-xs font-medium">{t.reference_id}</td>
                    <td className="px-4 py-3"><Badge className={TYPE_COLORS[t.type] || 'bg-muted'}>{t.type}</Badge></td>
                    <td className="px-4 py-3">{t.supplier}</td>
                    <td className="px-4 py-3 text-muted-foreground">{t.date}</td>
                    <td className="px-4 py-3"><Badge className={STATUS_COLORS[t.status] || 'bg-muted'}>{t.status}</Badge></td>
                    <td className="px-4 py-3 text-right font-medium">{(t.amount || 0).toLocaleString('en', { minimumFractionDigits: 2 })}</td>
                    <td className="px-4 py-3 text-right font-medium text-warning">{t.outstanding > 0 ? t.outstanding.toLocaleString('en', { minimumFractionDigits: 2 }) : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent></Card>
      </div>
    </DashboardLayout>
  );
}
